import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import backend from "../../backend";

/**
 * AplicarOCModal — asigna el N° de OC del cliente a una CustomerOrder existente
 * que fue creada sin OC (típicamente por el import retroactivo de guías).
 *
 * Props:
 *   target  { coId, coNumber, clientName, guiaNumber, monto } | null
 *   onClose, onSaved
 *
 * Backend: PATCH /customerOrder/:coId con { clientOCNumber }.
 * El handler ya cascadea clientOC a la WO espejo automáticamente.
 */
const AplicarOCModal = ({ target, onClose, onSaved }) => {
  const [ocNumber, setOcNumber] = useState("");
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");

  useEffect(() => {
    if (!target) return;
    setOcNumber("");
    setError("");
  }, [target]);

  if (!target) return null;

  const submit = async () => {
    setError("");
    const v = ocNumber.trim();
    if (!v) { setError("N° de OC requerido"); return; }
    setSaving(true);
    try {
      const res = await backend.patch(
        `customerOrder/${target.coId}`,
        { clientOCNumber: v }
      );
      onSaved?.(res.data);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Error al aplicar OC");
    }
    setSaving(false);
  };

  return ReactDOM.createPortal(
    <div
      onMouseDown={e => { if (e.target === e.currentTarget && !saving) onClose?.(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 10001,
      }}
    >
      <div style={{
        background: "#1a1a1a", border: "1px solid #333", borderRadius: 8,
        width: "min(460px, 92vw)", padding: "18px 22px", color: "#ddd",
        fontSize: 12, fontFamily: "Arial, sans-serif",
      }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#fff" }}>
            Aplicar N° de OC
          </div>
          <button
            onClick={() => !saving && onClose?.()}
            style={{ background:"transparent", border:"none", color: "#cbd5e1", fontSize:18, cursor:"pointer", lineHeight:1 }}
          >×</button>
        </div>

        <div style={{
          background:"#161616", border:"1px solid #2e2e2e", borderRadius: 4,
          padding: "10px 12px", marginBottom: 14, fontSize: 11, lineHeight: 1.5,
        }}>
          <div style={{ color: "#cbd5e1", fontSize: 10, textTransform:"uppercase", letterSpacing: 0.5 }}>
            OC sin N° asignado
          </div>
          <div style={{ color:"#fff", marginTop: 4 }}>
            🧾 OC interna <strong style={{ color:"#f39c12" }}>#{target.coNumber}</strong>
            {target.clientName && <span style={{ color: "#aaa", marginLeft: 8 }}>· {target.clientName}</span>}
          </div>
          {target.guiaNumber && (
            <div style={{ color: "#aaa", marginTop: 4 }}>
              Guía referenciada: <strong style={{ color:"#3498db" }}>{target.guiaNumber}</strong>
              {target.monto != null && (
                <span style={{ marginLeft: 8 }}>· {new Intl.NumberFormat("es-CL", { style:"currency", currency:"CLP", maximumFractionDigits: 0 }).format(target.monto)}</span>
              )}
            </div>
          )}
          <div style={{ color: "#aaa", marginTop: 8, fontSize: 10 }}>
            El N° de OC se aplicará a la OC interna y se replicará a la OT espejo. Luego podés facturar la guía con el botón <em>Facturar guía</em>.
          </div>
        </div>

        <div>
          <div style={{
            fontSize: 9.5, fontWeight: 700, color: "#cbd5e1",
            textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 3,
          }}>N° OC del cliente *</div>
          <input
            className="bini-input bini-mono"
            value={ocNumber}
            onChange={e => setOcNumber(e.target.value)}
            placeholder="ej: 4501234567"
            autoFocus
            onKeyDown={e => { if (e.key === "Enter" && !saving) submit(); }}
          />
        </div>

        {error && (
          <div style={{
            color:"#e74c3c", fontSize: 11, marginTop: 10,
            background:"rgba(231,76,60,0.08)", padding:"6px 10px", borderRadius: 4,
            border:"1px solid rgba(231,76,60,0.3)",
          }}>{error}</div>
        )}

        <div style={{ display:"flex", justifyContent:"flex-end", gap: 8, marginTop: 14 }}>
          <button
            onClick={() => !saving && onClose?.()}
            disabled={saving}
            style={{
              background:"transparent", border:"1px solid #444",
              color:"#aaa", borderRadius: 5, padding:"7px 14px",
              fontSize: 12, cursor:"pointer",
            }}
          >Cancelar</button>
          <button
            onClick={submit}
            disabled={saving}
            style={{
              background:"#a78bfa", border:"none", color:"#15102b",
              borderRadius: 5, padding:"7px 16px",
              fontSize: 12, fontWeight: 700,
              cursor: saving ? "wait" : "pointer",
              opacity: saving ? 0.7 : 1,
            }}
          >{saving ? "Guardando…" : "Aplicar OC"}</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AplicarOCModal;
